import { fetchSegmentMappingConfig } from '@/services/segmentMapping'
import { IApp, type Models, type UI } from '@/types'
import _ from 'lodash'
import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import { pushFieldMapping } from '@/services'
import { fetchISESegmentMappingTemplates } from '@/services/segmentMapping'
import { 
  fetchSegmentationConfig, 
  fetchDefaultSegmentationConfig,
  saveSegmentationConfig 
} from '@/services/segmentationConfigService'
import {
  deriveRulesetLogicFromCriteria,
  normalizeSegmentLogic,
  rulesetToCriteria,
} from '@/utils/segmentRuleset'

export const useSegmentStore = defineStore('segments', () => {
  const segments = ref<Models.Segment[]>([])
  const usedDefaultSegments = ref<boolean>(false)
  const accountSettings = useAccountSettingsStore()
  const productId = 'ise_srp_import_segmentation'
  const appConfig = useJazelAppConfig()
  const originalConfig = ref<IISEFieldMapGetConfigResponse | null>(null)
  const mappingTemplates = ref<IISESegmentMapGetTemplateResponse | null>(null)

  const loadingStatus = reactive<{
    segments: IApp.ILoadingStatus
    saveSegments: IApp.ILoadingStatus
  }>({
    segments: IApp.ILoadingStatus.Idle,
    saveSegments: IApp.ILoadingStatus.Idle,
  })

  const criteriaHeaders: UI.TableHeader[] = [
    { key: 'field', title: 'Field', sortable: false },
    {
      key: 'operator',
      title: 'Operator',
      sortable: false,
    },
    {
      key: 'value',
      title: 'Value',
      sortable: false,
    },
    {
      key: 'logicalOperator',
      title: 'Logical Operator',
      sortable: false,
    },
    {
      key: 'actions',
      title: '',
      sortable: false,
      width: '50px',
    },
  ]

  const segmentHeaders: UI.TableHeader[] = [{ key: 'name', title: '', sortable: false }]

  const addNewCriteria = (criteriaId: string) => {
    const index = _.findIndex(segments.value, { id: criteriaId })
    if (index > -1) {
      const currentLogic = deriveRulesetLogicFromCriteria(segments.value[index].criteria as any, 'AND')
      segments.value[index].criteria.push({
        field: '',
        logicalOperator: currentLogic,
        operator: 'contains',
        value: '',
      })
    }
  }

  const removeCriteria = (segmentId: string, index: number) => {
    const segmentIndex = _.findIndex(segments.value, { id: segmentId })
    if (segmentIndex > -1) {
      segments.value[segmentIndex].criteria.splice(index, 1)
    }
  }

  const moveSegment = (id: string, direction: 'up' | 'down') => {
    const index = _.findIndex(segments.value, { id })
    if (index === -1) return

    if (direction === 'up' && index > 0) {
      const temp = segments.value[index]
      segments.value[index] = segments.value[index - 1]
      segments.value[index - 1] = temp
    } else if (direction === 'down' && index < segments.value.length - 1) {
      const temp = segments.value[index]
      segments.value[index] = segments.value[index + 1]
      segments.value[index + 1] = temp
    }
  }

  // Ensure we always have usable segments; if the account config is
  // missing or empty, fetch defaults from backend.
  const ensureDefaultSegments = async (config: IISESegmentMapGetConfigResponse | null) => {
    const cfg: any = config ? JSON.parse(JSON.stringify(config)) : {}
    cfg.Enable = cfg.Enable ?? true

    // Canonical shape we want to support from ISE is:
    // { Enable: boolean, segments: { all: {...}, new: {...}, used: {...}, cpo: {...} } }
    // For safety, also support an older shape where segments may live under `config`.
    if (!cfg.segments && cfg.config && typeof cfg.config === 'object') {
      cfg.segments = cfg.config
    }

    // If segments are still missing or empty, fetch defaults from backend
    if (!cfg.segments || (typeof cfg.segments === 'object' && Object.keys(cfg.segments).length === 0)) {
      const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
      const defaultsResponse = await fetchDefaultSegmentationConfig(iseEndpoint)
      
      if (defaultsResponse?.data?.segments) {
        cfg.segments = defaultsResponse.data.segments
        usedDefaultSegments.value = true
      } else {
        console.error('Failed to fetch default segmentation config from backend')
        // Fallback to minimal config if backend fails
        cfg.segments = {
          all: {
            name: 'All Vehicles',
            exclusive: false,
            priority: 1000,
            custom: false,
            ruleset: { logic: 'AND', rules: [] }
          }
        }
        usedDefaultSegments.value = true
      }
    } else {
      usedDefaultSegments.value = false
    }

    return cfg
  }

  const getMappingTemplates = async (): Promise<IISESegmentMapGetTemplateResponse | null> => {
    const endpoint = appConfig.jamsBackendUrl
    const token = accountSettings.sessionToken

    mappingTemplates.value = await fetchISESegmentMappingTemplates(endpoint, productId, token)
    return mappingTemplates.value
  }

  const getMappingConfig = async (opts?: { includeDraft?: boolean }) => {
    const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const accountId = accountSettings.accountId

    // Fetch from ISE backend instead of JAMS
    const response = await fetchSegmentationConfig(iseEndpoint, accountId, { includeDraft: !!opts?.includeDraft })
    
    // Convert ISE response to expected format
    const config = response?.data ? {
      Enable: true,
      segments: response.data.segments
    } : null
    
    const ensuredConfig = await ensureDefaultSegments(config)

    // Make a backup of the original config for comparison when saving
    originalConfig.value = ensuredConfig

    return ensuredConfig
  }

  const getSegments = async (opts?: { includeDraft?: boolean }) => {
    loadingStatus.segments = IApp.ILoadingStatus.Loading

    const config: any = await getMappingConfig(opts)

    // Config is now the full product config; segments live under `segments`
    const segmentsObj = config?.segments || {}
    const hasSegments = Object.keys(segmentsObj).length > 0
    loadingStatus.segments = hasSegments ? IApp.ILoadingStatus.Success : IApp.ILoadingStatus.Error

    // Convert object to array for UI
    const mapped: any[] = Object.entries(segmentsObj).map(([id, segmentData]: [string, any]) => {
      // Convert ruleset format to criteria for UI compatibility.
      // NOTE: Backend supports a single ruleset.logic (segment-level), not per-rule joins.
      const criteria = rulesetToCriteria(segmentData.ruleset)

      const seg: any = {
        id,
        name: segmentData.name,
        priority:
          typeof segmentData.priority === 'number'
            ? segmentData.priority
            : id === 'all'
              ? 1000
              : 999,
        custom: segmentData.custom ?? false,
        action: '',
        criteria,
        feed: 'Chrome', // Placeholder for type compatibility
        filter: {},
      }
      return seg as unknown as Models.Segment
    })

    // Sort by priority; always pin 'all' last
    const nonAll = mapped
      .filter((s: any) => (s as any).id !== 'all')
      .sort((a: any, b: any) => ((a as any).priority ?? 999) - ((b as any).priority ?? 999))
    const allSeg = mapped.find((s: any) => (s as any).id === 'all')
    segments.value = (allSeg ? [...nonAll, allSeg] : nonAll) as any
  }

  // Save config to segments.value before pushing to backend
  const saveSegments = async (isOnboarding: boolean = false) => {
    loadingStatus.segments = IApp.ILoadingStatus.Loading

    const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const accountId = accountSettings.accountId
    const token = accountSettings.sessionToken

    // Convert segments array back to object with segment IDs as keys
    const configObject: Record<string, any> = {}

    // compute priorities from UI order using 10/20/30 scheme; pin 'all' to 1000
    const nonAll = segments.value.filter((s: any) => (s as any).id !== 'all') as any[]
    nonAll.forEach((s: any, idx: number) => {
      ;(s as any).priority = (idx + 1) * 10
    })
    const allSeg = (segments.value as any[]).find((s: any) => (s as any).id === 'all')
    if (allSeg) (allSeg as any).priority = 1000

    for (const s of segments.value as any[]) {
      const derivedLogic = normalizeSegmentLogic(
        deriveRulesetLogicFromCriteria(s.criteria as any, 'AND'),
        'AND',
      )

      // Keep UI consistent: treat connector as segment-level and normalize all rows before save.
      if (Array.isArray(s.criteria)) {
        for (const c of s.criteria) {
          c.logicalOperator = derivedLogic
        }
      }

      // Convert criteria back to ruleset format
      const rules = (s.criteria || []).map((c: any) => ({
        type: 'rule',
        field: c.field,
        operator: c.operator,
        value: c.value,
      }))

      configObject[s.id] = {
        name: s.name,
        exclusive: false,
        priority: (s as any).priority ?? (s.id === 'all' ? 1000 : 999),
        custom: (s as any).custom ?? false,
        ruleset: {
          logic: derivedLogic,
          rules,
        },
      }
    }

    // Save to ISE backend instead of JAMS
    const success = await saveSegmentationConfig(
      iseEndpoint,
      accountId,
      configObject,
      isOnboarding,
      token
    )

    if (success) {
      loadingStatus.segments = IApp.ILoadingStatus.Success
    } else {
      loadingStatus.segments = IApp.ILoadingStatus.Error
    }

    return success
  }

  return {
    segments,
    usedDefaultSegments,
    criteriaHeaders,
    segmentHeaders,
    loadingStatus,

    addNewCriteria,
    moveSegment,
    getSegments,
    saveSegments,
    removeCriteria,
    getMappingTemplates,
    getMappingConfig,
  }
})
